import { useState, useMemo, useContext, useEffect } from "react";
import "../../../../assets/scss/colorfamilies/colorfamilies.scss";
import ListView from "../../../../components/Search/FilterList/ColorsComponents/ListView";
import EditView from "../../../../components/Search/FilterList/ColorsComponents/EditView";
import AddView from "../../../../components/Search/FilterList/ColorsComponents/AddView";
import { ProductContext } from "../../../../store";
import { PostData } from "../../../../services/FetchData";
import { toast } from "react-toastify";

const ColorFamilies = () => {
	const [viewMode, setViewMode] = useState("list"),
		[families, setFamilies] = useState([]),
		[searchKey, setSearchKey] = useState(""),
		[sortColumn, setSortColumn] = useState("familyName"),
		[sortDirection, setSortDirection] = useState("asc"),
		{ currentWebsiteId } = useContext(ProductContext),
		[loading, setLoading] = useState(false),
		[error, setError] = useState(null),
		[originalFamilies, setOriginalFamilies] = useState([]),
		[showDeleteModal, setShowDeleteModal] = useState(false),
		[newFamilies, setNewFamilies] = useState([{ id: "new-1", familyName: "", familyCode: "#000000", colorsIncluded: "", }]);

	useEffect(() => {
		if (currentWebsiteId) {
			loadColorFamilies();
		}
	}, [currentWebsiteId]);

	useEffect(() => {
		if (error) {
			const timer = setTimeout(() => {
				setError(null);
			}, 5000);
			return () => clearTimeout(timer);
		}
	}, [error]);

	const formatColorCode = (colorCode) => {
		if (!colorCode) return "";
		if (colorCode === "*" || colorCode === "#") return colorCode;
		return colorCode.startsWith("#") ? colorCode : `#${colorCode}`;
	};

	const loadColorFamilies = async () => {
		setLoading(true);
		setError(null);
		PostData("ms1", "api/v1/filters/getAllColorFamilies", { websiteId: currentWebsiteId }).then(response => {
			if (response !== "Invalid" && response !== "undefined") {
				if( response.status === "success" ){
					let colorFamilies	= response.data.colorFamilies
					setFamilies(colorFamilies)
					setOriginalFamilies(JSON.parse(JSON.stringify(colorFamilies)))
				}
			}
			setLoading(false)
		})
	};

	const filteredFamilies = useMemo(() => {
		let filtered = families.filter((family) => {
			const query = searchKey.toLowerCase();
			return (
				family.familyName.toLowerCase().includes(query) ||
				family.familyCode.toLowerCase().includes(query) ||
				family.colorsIncluded.toLowerCase().includes(query)
			);
		});

		filtered.sort((a, b) => {
			let aValue = a[sortColumn];
			let bValue = b[sortColumn];

			if (typeof aValue === "string" && typeof bValue === "string") {
				aValue = aValue.toLowerCase();
				bValue = bValue.toLowerCase();

				if (sortDirection === "asc") {
					return aValue.localeCompare(bValue);
				} else {
					return bValue.localeCompare(aValue);
				}
			}

			return 0;
		});
		return filtered;
	}, [families, searchKey, sortColumn, sortDirection]);

	const handleSort = (column) => {
		if (sortColumn === column) {
			setSortDirection(sortDirection === "asc" ? "desc" : "asc");
		} else {
			setSortColumn(column);
			setSortDirection("asc");
		}
	};

	const handleFamilyChange = (id, field, value) => {
		setFamilies(
			families.map((family) => {
				if (family.id === id) {
					if (
						field === "familyCode" &&
						(family.familyCode === "#" || family.familyCode === "*")
					) {
						return family;
					}

					const formattedValue =
						field === "familyCode" ? formatColorCode(value) : value;
					return { ...family, [field]: formattedValue };
				}
				return family;
			})
		);
	};

	const handleToggleSelect = (id) => {
		setFamilies(
			families.map((family) => {
				if (family.id === id) {
					if (family.familyCode === "#" || family.familyCode === "*") {
						return family;
					}
					return { ...family, selected: !family.selected };
				}
				return family;
			})
		);
	};

	const handleToggleSelectAll = () => {
		const selectableFamilies = families.filter(
			(family) => family.familyCode !== "#" && family.familyCode !== "*"
		);
		const allSelectableSelected = selectableFamilies.every(
			(family) => family.selected
		);

		setFamilies(
			families.map((family) => ({
				...family,
				selected:
					family.familyCode === "#" || family.familyCode === "*"
						? false
						: !allSelectableSelected,
			}))
		);
	};

	const selectableFamilies = families.filter(
		(family) => family.familyCode !== "#" && family.familyCode !== "*"
	);
	const allSelected =
		selectableFamilies.length > 0 &&
		selectableFamilies.every((family) => family.selected);
	const anySelected = families.some((family) => family.selected);

	const handleNewFamilyChange = (id, field, value) => {
		const formattedValue =
			field === "familyCode" ? formatColorCode(value) : value;

		setNewFamilies(
			newFamilies.map((family) =>
				family.id === id ? { ...family, [field]: formattedValue } : family
			)
		);
	};

	const handlePhotoChange = (id, url) => {
		const trimmedUrl = url ? url.trim() : "";
		const isValidImageUrl = trimmedUrl && (trimmedUrl.startsWith("http://") || trimmedUrl.startsWith("https://")) && trimmedUrl.length > 10;
		setNewFamilies(newFamilies.map((family) => family.id === id ? { ...family, url: trimmedUrl, urlPresent: isValidImageUrl,} : family));
	};

	const handleFamilyPhotoChange = (id, url) => {
		const trimmedUrl = url ? url.trim() : "";
		const isValidImageUrl = trimmedUrl && (trimmedUrl.startsWith("http://") || trimmedUrl.startsWith("https://")) && trimmedUrl.length > 10;
		setFamilies(families.map((family) => family.id === id ? { ...family, url: trimmedUrl, urlPresent: isValidImageUrl,} : family));
	};

	const handleDeleteItems = () => {
		const selectedIds = families.filter((f) => f.selected && f.familyCode !== "#" && f.familyCode !== "*").map((f) => f.id);
		if (!selectedIds.length) return;
		setShowDeleteModal(true);
	};

	const confirmDelete = async () => {
		const ids = families.filter((f) => f.selected && f.familyCode !== "#" && f.familyCode !== "*").map((f) => f.id);
		try {
			setLoading(true);
			setError(null);
			PostData("ms1", 'api/v1/filters/deleteColorFamilies', {websiteId: currentWebsiteId, ids}).then(response => {
				if(response !== "Invalid" && response !== "undefined"){
					if( response.status === "success" ){
						toast.success(response.message)
						loadColorFamilies()
					}
				}
				setShowDeleteModal(false);
				setLoading(false)
			})
		} catch (err) {
			console.error("Delete failed:", err);
			setError("Failed to delete color families: " + err.message);
		}
	};

	const cancelDelete = () => {
		setShowDeleteModal(false);
	};

	const handleAddRow = () => {
		const newId = `new-${Date.now()}`;
		setNewFamilies([
			...newFamilies,
			{
				id: newId,
				familyName: "",
				familyCode: "#000000",
				colorsIncluded: "",
				url: null,
				urlPresent: false,
			},
		]);
	};

	const handleDeleteRow = (id) => {
		setNewFamilies(newFamilies.filter((family) => family.id !== id));
	};

	const handleSaveEdit = async () => {
		const incompleteFamily = families.find((family) => family.familyName.trim() !== "" || family.familyCode.trim() !== "" || family.colorsIncluded.trim() !== "");
		const completeFamilies = families.filter((family) => family.familyName.trim() !== "" && family.familyCode.trim() !== "" && family.colorsIncluded.trim() !== "");
		const hasIncompleteFamilies = incompleteFamily && completeFamilies.length !== families.length;
		if (hasIncompleteFamilies) {
			setError("Please fill in all fields (Family Name, Family Code, and Colors Included) for each color family before saving.");
			return;
		}

		try {
			setLoading(true);
			setError(null);
			const updates = [];
			families.forEach((family) => {
				const original = originalFamilies.find((orig) => orig.id === family.id);
				if (original) {
					const changedFields = { id: family.id };
					let hasChanges = false;
					if (family.familyName !== original.familyName) {
						changedFields.familyName = family.familyName;
						hasChanges = true;
					}
					if (family.familyCode !== original.familyCode) {
						changedFields.familyCode = family.familyCode === "*" || family.familyCode === "#" ? family.familyCode : family.familyCode.startsWith("#") ? family.familyCode : `#${family.familyCode}`;
						hasChanges = true;
					}
					if (family.colorsIncluded !== original.colorsIncluded) {
						changedFields.colorsIncluded = family.colorsIncluded;
						hasChanges = true;
					}
					if ( family.url !== original.url || family.urlPresent !== original.urlPresent ) {
						changedFields.url = family.url || null;
						changedFields.urlPresent = family.urlPresent || false;
						hasChanges = true;
					}
					if (hasChanges) {
						updates.push(changedFields);
					}
				}
			});
			if (updates.length > 0) {
				PostData("ms1", "api/v1/filters/updateColorFamilies", {websiteId: currentWebsiteId, colorFamilies: updates}).then(async (response) => {
					if(response !== "Invalid" && response !== "undefined"){
						if( response.status === "success" ){
							await loadColorFamilies();
							
						}
					}
					setViewMode("list");
					setLoading(false);
				})
			}
		} catch (err) {
			console.error("Update failed:", err);
			setError("Failed to update color families: " + err.message);
		}
	};

	const handleSaveAdd = async () => {
		const validNewFamilies = newFamilies.filter((family) => family.familyName.trim() !== "" && family.familyCode.trim() !== "" && family.colorsIncluded.trim() !== "" );
		const incompleteFamily = newFamilies.find((family) => family.familyName.trim() !== "" || family.familyCode.trim() !== "" || family.colorsIncluded.trim() !== "" );
		if (incompleteFamily && validNewFamilies.length !== newFamilies.length) {
			setError("Please fill in all fields (Family Name, Family Code, and Colors Included) for each color family before saving.");
			return;
		}
		if (validNewFamilies.length === 0) {
			setViewMode("list");
			return;
		}
		try {
			setLoading(true);
			setError(null);
			const colorFamilies = validNewFamilies.map((family) => ({familyName: family.familyName, familyCode: family.familyCode === "*" || family.familyCode === "#" ? family.familyCode : family.familyCode.startsWith("#") ? family.familyCode : `#${family.familyCode}`, colorsIncluded: family.colorsIncluded, url: family.url || null, urlPresent: family.urlPresent || false,}));
			PostData("ms1", "api/v1/filters/addColorFamilies", {websiteId: currentWebsiteId, colorFamilies}).then(async(response) => {
				if (response !== "Invalid" && response !== "undefined") {
					if( response.status === "success" ){
						setNewFamilies([{id: "new-1",familyName: "",familyCode: "#000000",colorsIncluded: "", url: null,},]);
						await loadColorFamilies();
						setViewMode("list");
					}
				}
				setLoading(false);
			})
		} catch (err) {
			console.error("Create failed:", err);
			setError("Failed to create color families: " + err.message);
		}
	};

	const handleCancel = () => {
		loadColorFamilies();
		setNewFamilies([{id: "new-1", familyName: "", familyCode: "#000000", colorsIncluded: "", url: null }]);
		setViewMode("list");
	};

	return (
		<div className="color-families">
			<div className="color-families__container">
				<div className="color-families__header">
					<div className="color-families__title-section">
						<h1 className="color-families__title">Color Families</h1>
					</div>
				</div>

				{error && (
					<div
						className="error-message"
						style={{
							backgroundColor: "#fee2e2",
							border: "1px solid #fecaca",
							color: "#dc2626",
							padding: "12px",
							borderRadius: "6px",
							margin: "16px 0",
						}}
					>
						{error}
					</div>
				)}

				{viewMode === "list" && (
					<ListView
						families={filteredFamilies}
						searchQuery={searchKey}
						onSearchChange={setSearchKey}
						onEdit={() => setViewMode("edit")}
						onAdd={() => setViewMode("add")}
						sortColumn={sortColumn}
						sortDirection={sortDirection}
						onSort={handleSort}
						onToggleSelect={handleToggleSelect}
						onToggleSelectAll={handleToggleSelectAll}
						allSelected={allSelected}
						anySelected={anySelected}
						onDeleteItems={handleDeleteItems}
					/>
				)}

				{viewMode === "edit" && (
					<EditView
						families={filteredFamilies}
						searchQuery={searchKey}
						onSearchChange={setSearchKey}
						onCancel={handleCancel}
						onSave={handleSaveEdit}
						onFamilyChange={handleFamilyChange}
						onToggleSelect={handleToggleSelect}
						onToggleSelectAll={handleToggleSelectAll}
						allSelected={allSelected}
						sortColumn={sortColumn}
						sortDirection={sortDirection}
						onSort={handleSort}
						anySelected={anySelected}
						onDeleteItems={handleDeleteItems}
						onFamilyPhotoChange={handleFamilyPhotoChange}
					/>
				)}

				{viewMode === "add" && (
					<AddView
						newFamilies={newFamilies}
						onCancel={handleCancel}
						onSave={handleSaveAdd}
						onFamilyChange={handleNewFamilyChange}
						onAddRow={handleAddRow}
						onDeleteRow={handleDeleteRow}
						onPhotoChange={handlePhotoChange}
					/>
				)}

				{showDeleteModal && (
					<div
						className="modal-overlay"
						style={{
							position: "fixed",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							backgroundColor: "rgba(0, 0, 0, 0.5)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							zIndex: 1000,
						}}
					>
						<div
							className="modal-content"
							style={{
								backgroundColor: "white",
								padding: "24px",
								borderRadius: "8px",
								boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
								maxWidth: "400px",
								width: "90%",
							}}
						>
							<h3
								style={{
									margin: "0 0 16px 0",
									fontSize: "18px",
									fontWeight: "bold",
								}}
							>
								Confirm Delete
							</h3>
							<p style={{ margin: "0 0 24px 0", color: "#666" }}>
								Are you sure you want to delete the selected color families?
								This action cannot be undone.
							</p>
							<div
								style={{
									display: "flex",
									gap: "12px",
									justifyContent: "flex-end",
								}}
							>
								<button
									onClick={cancelDelete}
									style={{
										padding: "8px 16px",
										border: "1px solid #ccc",
										borderRadius: "4px",
										backgroundColor: "white",
										cursor: "pointer",
									}}
								>
									Cancel
								</button>
								<button
									onClick={confirmDelete}
									disabled={loading}
									style={{
										padding: "8px 16px",
										border: "none",
										borderRadius: "4px",
										backgroundColor: "#dc2626",
										color: "white",
										cursor: loading ? "not-allowed" : "pointer",
										opacity: loading ? 0.6 : 1,
									}}
								>
									{loading ? "Deleting..." : "Delete"}
								</button>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default ColorFamilies;
